import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { canAccessModule } from "../lib/navigation";
import {
  hasAnyRole,
  resolveEnterpriseRoles,
  type RoleCodeValue,
} from "../lib/rbac";

interface RequireRoleProps {
  /** Master Blueprint module id — matches `lib/navigation.ts` (e.g. "gl", "crm"). */
  moduleId?: string;
  /** Explicit 0-based role codes. Takes precedence over `allowedRoleNames`. */
  allowedRoles?: RoleCodeValue[];
  /** Enterprise string labels such as "SuperAdmin" or "FinancialAdvisor". */
  allowedRoleNames?: string[];
  children?: ReactNode;
}

export function RequireRole({
  moduleId,
  allowedRoles,
  allowedRoleNames,
  children,
}: RequireRoleProps) {
  const { profile } = useAuth();
  const { verticalCode } = useOrg();

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  let permitted = false;

  if (allowedRoles && allowedRoles.length > 0) {
    permitted = hasAnyRole(profile.roles, allowedRoles);
  } else if (allowedRoleNames && allowedRoleNames.length > 0) {
    const resolved = resolveEnterpriseRoles(allowedRoleNames);
    permitted = resolved.length > 0 && hasAnyRole(profile.roles, resolved);
  } else if (moduleId) {
    permitted = canAccessModule(moduleId, profile.roles, verticalCode);
  }

  if (!permitted) {
    return <Navigate to="/forbidden" replace />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
}
